import { useState } from "react"
import { useTranslation } from "react-i18next"

import { detectLanguage, isTranslationSupported } from "src/services/api/translate"
import { useLogger, useLanguages } from "src/providers"
import { snackbarErrorHandler } from "src/utils/errorHandler"
import { useSnackbar } from "src/providers/SnackbarProvider"
import { useLocalStorage } from "src/utils/useLocalStorage"
import { Language } from "src/utils/useLanguageList"

const TYPING_DELAY = 1000
const MAX_INPUT_LENGTH = 1500

// Custom hook for language management
const useLanguageManagement = () => {
  const { snackbarShowMessage } = useSnackbar()
  const { logger } = useLogger()
  const { t } = useTranslation("common")
  const { t: tLang } = useTranslation("lang")
  const { allUserLanguages, aiTextTranslatorDefaultLangFrom, aiTextTranslatorDefaultLangTo } = useLanguages()

  const [langFrom, setLangFrom] = useLocalStorage<Language>(
    "aiTextTranslator.langFrom",
    aiTextTranslatorDefaultLangFrom,
    (value: Language) => allUserLanguages.some((l) => l.languageId === value.languageId),
    [allUserLanguages, aiTextTranslatorDefaultLangFrom]
  )

  const [langTo, setLangTo] = useLocalStorage<Language>(
    "aiTextTranslator.langTo",
    aiTextTranslatorDefaultLangTo,
    (value: Language) => allUserLanguages.some((l) => l.languageId === value.languageId),
    [allUserLanguages, aiTextTranslatorDefaultLangTo]
  )
  const [supported, setSupported] = useState<boolean>(true)
  const [langSuggestion, setLangSuggestion] = useState<string>("")
  const [translateDelay, setTranslateDelay] = useState<any>()

  const checkEngineSupport = async () => {
    try {
      if (langFrom.languageId === "auto") return
      const sup = (await isTranslationSupported(langFrom.languageId, langTo.languageId)) || false
      if (!sup) snackbarShowMessage(t("translate.notSupported"), "warning")
      setSupported(sup)
    } catch (e) {
      snackbarErrorHandler(e, { snackbarShowMessage }, t)
      logger.error("System - Failed to check engine support")
    }
  }

  const startDetectionDelay = (text: string) => {
    if (translateDelay) {
      clearTimeout(translateDelay)
      setTranslateDelay(setTimeout(() => detect(text), TYPING_DELAY))
    } else {
      setTranslateDelay(setTimeout(() => detect(text), TYPING_DELAY))
    }
  }

  const detect = async (text: string): Promise<void> => {
    try {
      if (!text || text.length > MAX_INPUT_LENGTH) return
      detectLanguage(text).then((detectedLanguages) => {
        setLangSuggestion(detectedLanguages.data[0]?.lang || "")
      })
    } catch (e) {
      logger.error("System - Failed to detect language")
    }
  }

  const swap = () => {
    const prevSource = langFrom
    setLangFrom(langTo)
    setLangTo(prevSource)
  }

  const langsSelected = (): boolean => {
    return !!langFrom && !!langTo
  }

  const getSuggestionHelperText = () => {
    if (!!langSuggestion && langSuggestion !== langFrom.languageId.split("_")[0]) {
      return `${tLang("langSuggestion")} ${tLang(langSuggestion)}`
    }
    return undefined
  }

  return {
    langFrom,
    setLangFrom,
    langTo,
    setLangTo,
    supported,
    checkEngineSupport,
    startDetectionDelay,
    swap,
    langsSelected,
    getSuggestionHelperText,
    allUserLanguages
  }
}

export default useLanguageManagement
